import { DeleteOperation, IdPathParameters } from '@infuse/api/common';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericDeleteRole = ({
  apiFn,
  forcedParentId
}: {
  apiFn: DeleteOperation<never> | DeleteOperation<IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; roleId: string }>({
    mutationFn: async ({ parentId, roleId }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn({ id: parentId, roleId: roleId });

        notify({
          title: t('iam.roles.header.delete_role'),
          message: t('iam.roles.notify.delete_role_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.delete_role'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.delete_role_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    }
  });

  return {
    ...mutation
  };
};
